import { useState } from "react";
import type { FuelPlanRoute } from "./api";

interface RouteEfficiencySummaryProps {
    routes: FuelPlanRoute[];
    currentRouteIndex: number;
}

const RouteEfficiencySummary = ({ routes, currentRouteIndex }: RouteEfficiencySummaryProps) => {
    console.log(`current route index: ${currentRouteIndex}`);
    const [showTable, setShowTable] = useState(true);

    if (!routes || !Array.isArray(routes)) return null;

    // Get route classification based on its properties
    const getRouteClassification = (route: FuelPlanRoute, index: number) => {
        // Use ranking property if available or fallback to index-based naming
        if (route.routeType) return route.routeType;
        if (route.optimalRanking === 1) return "Rank One";
        if (route.optimalRanking === 2) return "Rank Two";
        if (route.optimalRanking === 3) return "Rank Three";
        return `Rank ${index + 1}`;
    };

    // Find the minimum totalMiles across all routes for mileage comparison
    const minTotalMiles = Math.min(
        ...routes.filter((route) => route.totalMiles && route.totalMiles > 0).map((route) => route.totalMiles),
        Infinity
    );

    // Find the minimum totalCost across all routes for cost comparison
    const minTotalCost = Math.min(
        ...routes.filter((route) => route.totalCost && route.totalCost > 0).map((route) => route.totalCost),
        Infinity
    );

    return (
        <div className="bg-white border-b border-gray-200">
            <div
                className="flex justify-between items-center px-3 py-1.5 bg-gray-50 border-b border-gray-200"
                onClick={() => setShowTable(!showTable)}
            >
                <h2 className="text-sm font-medium text-gray-800 flex items-center">
                    <span className="w-1 h-1 bg-orange-500 rounded-full mr-1.5"></span>
                    Route Comparison
                </h2>
                <button className="text-gray-600 hover:text-orange-600 transition-colors px-1.5 py-0.5 rounded text-xs">
                    {showTable ? "Hide ▲" : "Show ▼"}
                </button>
            </div>

            {showTable ? (
                <div className="overflow-x-auto transition-all duration-300 border-b border-gray-200">
                    <table className="min-w-full bg-white text-xs table-fixed">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-1 py-1.5 text-left font-medium text-gray-700 uppercase tracking-wider w-[80px] text-[10px]">
                                    Route
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[50px] text-[10px]">
                                    Miles
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[45px] text-[10px]">
                                    Average MPH
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[40px] text-[10px]">
                                    Duration (hrs)
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[60px] text-[10px]">
                                    Toll Cost
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[65px] text-[10px]">
                                    IFTA Cost
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[90px] text-[10px]">
                                    Pre-IFTA Fuel Cost
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[90px] text-[10px]">
                                    Optimal Price
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[70px] text-[10px]">
                                    Total Cost
                                </th>
                                <th className="px-1 py-1.5 text-right font-medium text-gray-700 uppercase tracking-wider w-[50px] text-[10px]">
                                    CPM
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {routes.map((route, index) => {
                                // Find the original index of this route to determine if it's selected

                                const isCurrentRoute = index === currentRouteIndex;

                                // Determine color based on ranking
                                let rankCircleClass = "bg-gray-500";
                                if (route.optimalRanking === 1) rankCircleClass = "bg-green-500";
                                else if (route.optimalRanking === 2) rankCircleClass = "bg-blue-500";
                                else if (route.optimalRanking === 3) rankCircleClass = "bg-red-500";

                                // Get route classification
                                const routeClassification = getRouteClassification(route, index);

                                // Calculate differences from min values
                                const milesDifference = route.totalMiles - minTotalMiles;
                                const totalCostDifference = route.totalCost - minTotalCost;

                                return (
                                    <tr
                                        key={`route-row-${index}`}
                                        className={`border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150 ${
                                            isCurrentRoute ? "bg-orange-50" : ""
                                        }`}
                                    >
                                        <td className="px-1 py-1.5 w-[80px]">
                                            <div className="flex items-center space-x-1">
                                                <div
                                                    className={`${rankCircleClass} text-white rounded-full w-5 h-5 flex items-center justify-center font-medium text-xs flex-shrink-0`}
                                                >
                                                    {route.optimalRanking || index + 1}
                                                </div>
                                                <span className="text-xs font-medium whitespace-nowrap">
                                                    {routeClassification}
                                                </span>
                                                {isCurrentRoute && (
                                                    <span className="text-xs bg-orange-100 text-orange-800 rounded px-1 font-medium whitespace-nowrap ml-1">
                                                        Current
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium w-[75px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    {route.totalMiles
                                                        ? Number(route.totalMiles).toLocaleString("en-US", {
                                                              maximumFractionDigits: 1,
                                                              minimumFractionDigits: 1,
                                                          })
                                                        : "0"}
                                                </div>
                                                {route.totalMiles &&
                                                    minTotalMiles > 0 &&
                                                    minTotalMiles < Infinity &&
                                                    route.totalMiles > minTotalMiles && (
                                                        <div
                                                            className={`text-[9px] ${
                                                                milesDifference > 0 ? "text-red-600" : "text-green-600"
                                                            }`}
                                                        >
                                                            {milesDifference > 0 ? "+" : ""}
                                                            {Number(milesDifference).toLocaleString("en-US", {
                                                                maximumFractionDigits: 1,
                                                                minimumFractionDigits: 1,
                                                            })}
                                                        </div>
                                                    )}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 w-[45px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    {route.averageMPH
                                                        ? Number(route.averageMPH).toLocaleString("en-US", {
                                                              maximumFractionDigits: 1,
                                                              minimumFractionDigits: 1,
                                                          })
                                                        : "0"}
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 w-[40px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    {route.duration
                                                        ? Number(route.duration).toLocaleString("en-US", {
                                                              maximumFractionDigits: 1,
                                                              minimumFractionDigits: 1,
                                                          })
                                                        : "0"}
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium text-blue-700 w-[60px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    $
                                                    {route.tollCost
                                                        ? Number(route.tollCost).toLocaleString("en-US", {
                                                              maximumFractionDigits: 2,
                                                              minimumFractionDigits: 2,
                                                          })
                                                        : "0.00"}
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium text-purple-700 w-[65px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    $
                                                    {route.iftaTaxPerMile && route.totalMiles
                                                        ? Number(
                                                              route.iftaTaxPerMile * route.totalMiles
                                                          ).toLocaleString("en-US", {
                                                              maximumFractionDigits: 2,
                                                              minimumFractionDigits: 2,
                                                          })
                                                        : "0.00"}
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium text-rose-700 w-[90px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    $
                                                    {route.preIftafuelcost
                                                        ? Number(route.preIftafuelcost).toLocaleString("en-US", {
                                                              maximumFractionDigits: 2,
                                                              minimumFractionDigits: 2,
                                                          })
                                                        : route.preIftaFuelPM && route.totalMiles
                                                        ? Number(route.preIftaFuelPM * route.totalMiles).toLocaleString(
                                                              "en-US",
                                                              { maximumFractionDigits: 2, minimumFractionDigits: 2 }
                                                          )
                                                        : "0.00"}
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium text-amber-700 w-[90px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    $
                                                    {route.optimalPrice
                                                        ? Number(route.optimalPrice).toLocaleString("en-US", {
                                                              maximumFractionDigits: 2,
                                                              minimumFractionDigits: 2,
                                                          })
                                                        : "0.00"}
                                                    /gal
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium text-green-700 w-[90px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    $
                                                    {route.totalCost
                                                        ? Number(route.totalCost).toLocaleString("en-US", {
                                                              maximumFractionDigits: 2,
                                                              minimumFractionDigits: 2,
                                                          })
                                                        : "0.00"}
                                                </div>
                                                {route.totalCost &&
                                                    minTotalCost > 0 &&
                                                    minTotalCost < Infinity &&
                                                    route.totalCost > minTotalCost && (
                                                        <div
                                                            className={`text-[9px] ${
                                                                totalCostDifference > 0
                                                                    ? "text-red-600"
                                                                    : "text-green-600"
                                                            }`}
                                                        >
                                                            {totalCostDifference > 0 ? "+" : ""}
                                                            {Number(totalCostDifference).toLocaleString("en-US", {
                                                                maximumFractionDigits: 2,
                                                                minimumFractionDigits: 2,
                                                            })}
                                                        </div>
                                                    )}
                                            </div>
                                        </td>
                                        <td className="px-1 py-1 font-medium text-cyan-700 w-[50px]">
                                            <div className="flex flex-col items-end">
                                                <div>
                                                    {route.totalCost && minTotalMiles && minTotalMiles < Infinity
                                                        ? `$${Number(route.totalCost / minTotalMiles).toLocaleString(
                                                              "en-US",
                                                              { maximumFractionDigits: 2, minimumFractionDigits: 2 }
                                                          )}`
                                                        : "$0.00"}
                                                </div>
                                                {route.optimalRanking !== 1 && <div className="h-[14px]"></div>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
};

export default RouteEfficiencySummary;
